import React, { useId } from 'react'
import { Loader2, X } from 'lucide-react'
import { EntitySearchInput, TypeaheadListbox } from '../../../components/search'
import type { CustomerSuggestion } from './types'
import styles from '../CreateTransaction.module.css'

export type CustomerPanelProps = {
  customerName: string
  customerEmail: string
  customerPhone: string
  notes: string
  onCustomerFieldChange: (field: 'name' | 'email' | 'phone', value: string) => void
  onCustomerFieldFocus: (field: 'name' | 'email' | 'phone') => void
  onSuggestionKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onNotesChange: (value: string) => void
  onClose: () => void
  onSave: () => void
  savingCustomerDetails: boolean
  shouldShowCustomerSuggestionPanel: (field: 'name' | 'email' | 'phone') => boolean
  filteredCustomerSuggestions: CustomerSuggestion[]
  selectCustomerSuggestion: (suggestion: CustomerSuggestion) => void
  customerSearchLoading: boolean
  customerSearchError: string | null
  activeSuggestion: number
  setActiveSuggestion: React.Dispatch<React.SetStateAction<number>>
  customerNameRef: React.RefObject<HTMLInputElement | null>
  customerEmailRef: React.RefObject<HTMLInputElement | null>
  customerPhoneRef: React.RefObject<HTMLInputElement | null>
  suggestionsRef: React.RefObject<HTMLDivElement | null>
}

export const CustomerPanel: React.FC<CustomerPanelProps> = ({
  customerName,
  customerEmail,
  customerPhone,
  notes,
  onCustomerFieldChange,
  onCustomerFieldFocus,
  onSuggestionKeyDown,
  onNotesChange,
  onClose,
  onSave,
  savingCustomerDetails,
  shouldShowCustomerSuggestionPanel,
  filteredCustomerSuggestions,
  selectCustomerSuggestion,
  customerSearchLoading,
  customerSearchError,
  activeSuggestion,
  setActiveSuggestion,
  customerNameRef,
  customerEmailRef,
  customerPhoneRef,
  suggestionsRef,
}) => {
  const baseId = useId()
  const nameListId = `${baseId}-customer-typeahead-name`
  const emailListId = `${baseId}-customer-typeahead-email`
  const phoneListId = `${baseId}-customer-typeahead-phone`

  return (
    <div className={styles.customerPanel}>
      <div className={styles.customerPanelRow}>
        <strong>Customer</strong>
        <button type="button" className={styles.customerPanelClose} onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      <div className={styles.customerPanelGrid}>
        <div className={styles.customerPanelField}>
          <div className={styles.customerPanelLabel}>Name</div>
          <EntitySearchInput
            ref={customerNameRef}
            id={`${baseId}-customer-name`}
            inputClassName={styles.customerPanelInput}
            placeholder="Enter customer name"
            value={customerName}
            onChange={(e) => onCustomerFieldChange('name', e.target.value)}
            onFocus={() => onCustomerFieldFocus('name')}
            onKeyDown={onSuggestionKeyDown}
            aria-controls={shouldShowCustomerSuggestionPanel('name') ? nameListId : undefined}
            aria-expanded={shouldShowCustomerSuggestionPanel('name')}
            aria-autocomplete="list"
          />
          {shouldShowCustomerSuggestionPanel('name') && (
            <TypeaheadListbox id={nameListId} listRef={suggestionsRef} className={styles.customerSuggestions}>
              {customerSearchLoading && (
                <div className={styles.customerSuggestionStatus}>
                  <Loader2 className={styles.spinner} size={16} aria-hidden />
                  Searching…
                </div>
              )}
              {customerSearchError && !customerSearchLoading && (
                <div className={styles.customerSuggestionStatus} role="alert">
                  {customerSearchError}
                </div>
              )}
              {!customerSearchLoading &&
                !customerSearchError &&
                filteredCustomerSuggestions.length === 0 &&
                customerName.trim().length >= 2 && (
                  <div className={styles.customerSuggestionStatus}>No matching customers</div>
                )}
              {!customerSearchLoading &&
                !customerSearchError &&
                filteredCustomerSuggestions.map((s, i) => (
                  <button
                    key={`${s.customerName}-panel-name-${i}`}
                    type="button"
                    role="option"
                    aria-selected={i === activeSuggestion}
                    className={`${styles.customerSuggestionItem} ${i === activeSuggestion ? styles.customerSuggestionActive : ''}`}
                    onClick={() => selectCustomerSuggestion(s)}
                    onMouseEnter={() => setActiveSuggestion(i)}
                  >
                    <span className={styles.customerSuggestionName}>{s.customerName}</span>
                    <span className={styles.customerSuggestionMeta}>
                      {s.tradeCount} trade{s.tradeCount !== 1 ? 's' : ''}
                      {s.customerEmail && ` · ${s.customerEmail}`}
                    </span>
                  </button>
                ))}
            </TypeaheadListbox>
          )}
        </div>
        <div className={styles.customerPanelField}>
          <div className={styles.customerPanelLabel}>Email</div>
          <EntitySearchInput
            ref={customerEmailRef}
            id={`${baseId}-customer-email`}
            type="email"
            inputClassName={styles.customerPanelInput}
            placeholder="customer@example.com"
            value={customerEmail}
            onChange={(e) => onCustomerFieldChange('email', e.target.value)}
            onFocus={() => onCustomerFieldFocus('email')}
            onKeyDown={onSuggestionKeyDown}
            aria-controls={shouldShowCustomerSuggestionPanel('email') ? emailListId : undefined}
            aria-expanded={shouldShowCustomerSuggestionPanel('email')}
            aria-autocomplete="list"
          />
          {shouldShowCustomerSuggestionPanel('email') && (
            <TypeaheadListbox id={emailListId} listRef={suggestionsRef} className={styles.customerSuggestions}>
              {customerSearchLoading && (
                <div className={styles.customerSuggestionStatus}>
                  <Loader2 className={styles.spinner} size={16} aria-hidden />
                  Searching…
                </div>
              )}
              {customerSearchError && !customerSearchLoading && (
                <div className={styles.customerSuggestionStatus} role="alert">
                  {customerSearchError}
                </div>
              )}
              {!customerSearchLoading &&
                !customerSearchError &&
                filteredCustomerSuggestions.length === 0 &&
                customerEmail.trim().length >= 2 && (
                  <div className={styles.customerSuggestionStatus}>No matching customers</div>
                )}
              {!customerSearchLoading &&
                !customerSearchError &&
                filteredCustomerSuggestions.map((s, i) => (
                  <button
                    key={`${s.customerName}-panel-email-${i}`}
                    type="button"
                    role="option"
                    aria-selected={i === activeSuggestion}
                    className={`${styles.customerSuggestionItem} ${i === activeSuggestion ? styles.customerSuggestionActive : ''}`}
                    onClick={() => selectCustomerSuggestion(s)}
                    onMouseEnter={() => setActiveSuggestion(i)}
                  >
                    <span className={styles.customerSuggestionName}>{s.customerName}</span>
                    <span className={styles.customerSuggestionMeta}>
                      {s.customerEmail || 'No email'}
                      {s.customerPhone && ` · ${s.customerPhone}`}
                    </span>
                  </button>
                ))}
            </TypeaheadListbox>
          )}
        </div>
        <div className={styles.customerPanelField}>
          <div className={styles.customerPanelLabel}>Phone</div>
          <EntitySearchInput
            ref={customerPhoneRef}
            id={`${baseId}-customer-phone`}
            type="tel"
            inputClassName={styles.customerPanelInput}
            placeholder="+61 4xx xxx xxx"
            value={customerPhone}
            onChange={(e) => onCustomerFieldChange('phone', e.target.value)}
            onFocus={() => onCustomerFieldFocus('phone')}
            onKeyDown={onSuggestionKeyDown}
            aria-controls={shouldShowCustomerSuggestionPanel('phone') ? phoneListId : undefined}
            aria-expanded={shouldShowCustomerSuggestionPanel('phone')}
            aria-autocomplete="list"
          />
          {shouldShowCustomerSuggestionPanel('phone') && (
            <TypeaheadListbox id={phoneListId} listRef={suggestionsRef} className={styles.customerSuggestions}>
              {customerSearchLoading && (
                <div className={styles.customerSuggestionStatus}>
                  <Loader2 className={styles.spinner} size={16} aria-hidden />
                  Searching…
                </div>
              )}
              {customerSearchError && !customerSearchLoading && (
                <div className={styles.customerSuggestionStatus} role="alert">
                  {customerSearchError}
                </div>
              )}
              {!customerSearchLoading &&
                !customerSearchError &&
                filteredCustomerSuggestions.length === 0 &&
                customerPhone.trim().length >= 2 && (
                  <div className={styles.customerSuggestionStatus}>No matching customers</div>
                )}
              {!customerSearchLoading &&
                !customerSearchError &&
                filteredCustomerSuggestions.map((s, i) => (
                  <button
                    key={`${s.customerName}-panel-phone-${i}`}
                    type="button"
                    role="option"
                    aria-selected={i === activeSuggestion}
                    className={`${styles.customerSuggestionItem} ${i === activeSuggestion ? styles.customerSuggestionActive : ''}`}
                    onClick={() => selectCustomerSuggestion(s)}
                    onMouseEnter={() => setActiveSuggestion(i)}
                  >
                    <span className={styles.customerSuggestionName}>{s.customerName}</span>
                    <span className={styles.customerSuggestionMeta}>
                      {s.customerPhone || 'No phone'}
                      {s.customerEmail && ` · ${s.customerEmail}`}
                    </span>
                  </button>
                ))}
            </TypeaheadListbox>
          )}
        </div>
      </div>
      <div className={styles.customerPanelNotes}>
        <div className={styles.customerPanelLabel}>Notes</div>
        <textarea
          className={styles.customerPanelTextarea}
          placeholder="Add any additional notes about this trade"
          rows={3}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </div>
      <div className={styles.customerPanelActions}>
        <button
          type="button"
          className={styles.customerPanelSave}
          onClick={() => void onSave()}
          disabled={savingCustomerDetails}
        >
          {savingCustomerDetails ? 'Saving...' : 'Save Customer Info'}
        </button>
      </div>
    </div>
  )
}
